import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CreditCard, X, AlertCircle, Loader2, CheckCircle, DollarSign, Banknote
} from 'lucide-react';
import { depositsAPI, paymentsAPI } from '../services/api';
import useAuthStore from '../context/authStore';
import { formatMoney } from '../utils/format';
import { HostedCardForm, usePaymentGateway, isHostedGateway } from './payments/CardForm';

// Card brand icons/colors
const cardBrands = {
  visa: { color: 'bg-blue-600', label: 'Visa' },
  mastercard: { color: 'bg-red-500', label: 'Mastercard' },
  amex: { color: 'bg-blue-400', label: 'Amex' },
  discover: { color: 'bg-orange-500', label: 'Discover' },
  unknown: { color: 'bg-neutral-500', label: 'Card' },
};

// Format card number with spaces
function formatCardNumber(value) {
  const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
  const matches = v.match(/\d{4,16}/g);
  const match = (matches && matches[0]) || '';
  const parts = [];
  for (let i = 0, len = match.length; i < len; i += 4) {
    parts.push(match.substring(i, i + 4));
  }
  return parts.length ? parts.join(' ') : value;
}

// Format currency
function formatCurrency(amount) {
  return formatMoney(amount);
}

// Helper function to calculate credit card surcharge based on settings
function calculateSurcharge(amount, settings) {
  if (!settings?.creditCardSurchargeEnabled) return 0;
  if (settings.creditCardSurchargeType === 'fixed') {
    return settings.creditCardSurchargeValue || 0;
  }
  // percentage
  const rate = (settings.creditCardSurchargeValue || 0) / 100;
  return amount * rate;
}

// Helper function to format surcharge display text
function formatSurchargeText(settings) {
  if (!settings?.creditCardSurchargeEnabled) return null;
  if (settings.creditCardSurchargeType === 'fixed') {
    return `$${settings.creditCardSurchargeValue?.toFixed(2) || '0.00'} surcharge`;
  }
  return `${settings.creditCardSurchargeValue || 0}% surcharge`;
}

/**
 * PayDepositModal - Modal for paying quote deposits
 *
 * Props:
 * - isOpen: boolean to control visibility
 * - onClose: function to close the modal
 * - onSuccess: function called after successful payment
 * - deposit: deposit object with deposit_amount, cw_opportunity_id, amount_paid, status
 * - quoteName: name of the quote for display
 * - isPublic: whether this is a public payment (shared link)
 * - paymentToken: token for public payment
 * - paymentSettings: payment settings from portal (allowCreditCard, allowACH, surcharge)
 */
export default function PayDepositModal({
  isOpen,
  onClose,
  onSuccess,
  deposit,
  quoteName,
  isPublic = false,
  paymentToken = null,
  paymentSettings = null,
  gateway: gatewayProp = null,
}) {
  // Default payment settings if not provided
  const settings = paymentSettings || {
    allowCreditCard: true,
    allowACH: true,
    creditCardSurchargeEnabled: true,
    creditCardSurchargeType: 'percentage',
    creditCardSurchargeValue: 4
  };
  // Extract values from deposit object — APIs return camelCase
  const opportunityId = deposit?.opportunityId || deposit?.cw_opportunity_id;
  const depositAmount = deposit?.status === 'partial'
    ? (deposit.depositAmount || deposit.deposit_amount || 0) - (deposit.amountPaid || deposit.amount_paid || 0)
    : (deposit?.depositAmount || deposit?.deposit_amount || 0);

  // Get user from auth store for fetching payment methods
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  // Initialize default payment method from settings
  const defaultMethod = settings.defaultDepositMethod || 'card';

  // State hooks - must be called before any conditional returns
  const [selectedMethodId, setSelectedMethodId] = useState(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [useNewBank, setUseNewBank] = useState(false);
  const [saveCard, setSaveCard] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Bank account form state
  const [bankData, setBankData] = useState({
    routingNumber: '',
    accountNumber: '',
    accountType: 'checking',
    nameOnAccount: '',
    bankName: '',
  });

  // Card form state (for new card or public payment)
  const [cardData, setCardData] = useState({
    cardNumber: '',
    expirationMonth: '',
    expirationYear: '',
    cvv: '',
  });
  const [billingAddress, setBillingAddress] = useState({
    firstName: '',
    lastName: '',
    company: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    country: 'USA',
  });

  // Fetch payment methods (authenticated users only)
  const { data: methodsData } = useQuery({
    queryKey: ['paymentMethods', companyId],
    queryFn: () => paymentsAPI.getMethods({ companyId }),
    enabled: !isPublic && !!companyId,
  });

  // Filter payment methods based on settings
  const allPaymentMethods = methodsData?.data?.paymentMethods || [];
  const paymentMethods = allPaymentMethods.filter(method => {
    if (method.payment_type === 'card' && !settings.allowCreditCard) return false;
    if (method.payment_type === 'bank' && !settings.allowACH) return false;
    return true;
  });

  // Surcharge applies only when paying by credit card (not ACH).
  const selectedMethod = paymentMethods.find(m => m.id === selectedMethodId);
  const isCardPayment = useNewBank
    ? false  // ACH form shown — no surcharge
    : isPublic
      || selectedMethod?.payment_type === 'card'
      || (!isPublic && useNewCard)
      || (!isPublic && !useNewBank && paymentMethods.length === 0);

  // Calculate surcharge for credit card payments
  const surchargeAmount = isCardPayment ? calculateSurcharge(depositAmount, settings) : 0;
  const totalAmount = depositAmount + surchargeAmount;
  const surchargeText = formatSurchargeText(settings);

  // Apply default payment method based on settings.defaultDepositMethod.
  // Runs when settings or paymentMethods load (useEffect so it catches async data).
  useEffect(() => {
    if (!settings.allowACH && !settings.allowCreditCard) return;
    const preferACH = defaultMethod === 'ach' && settings.allowACH;

    if (isPublic) {
      // Public: control which form tab is shown
      setUseNewBank(preferACH);
    } else if (paymentMethods.length > 0 && !selectedMethodId) {
      // Authenticated with saved methods: pre-select preferred type
      const preferred = preferACH
        ? (paymentMethods.find(m => m.payment_type === 'bank' && m.is_default)
            || paymentMethods.find(m => m.payment_type === 'bank')
            || paymentMethods.find(m => m.is_default)
            || paymentMethods[0])
        : (paymentMethods.find(m => m.is_default) || paymentMethods[0]);
      setSelectedMethodId(preferred.id);
    } else if (paymentMethods.length === 0 && !isPublic && preferACH) {
      // Authenticated, no saved methods, ACH default: show add-bank form
      setUseNewBank(true);
    }
  }, [defaultMethod, settings.allowACH, settings.allowCreditCard, isPublic, paymentMethods.length]);

  // Payment mutation for authenticated users
  const payMutation = useMutation({
    mutationFn: (data) => depositsAPI.pay(opportunityId, data),
    onSuccess: (response) => {
      setSuccess(true);
      setTimeout(() => {
        onSuccess?.(response.data);
      }, 1500);
    },
    onError: (err) => {
      // Phase 7d: a saved card whose bank wants the customer to confirm it.
      const data = err.response?.data;
      if (data?.requiresAction && data.actionId) {
        window.location.assign(`/payments/confirm/${data.actionId}`);
        return;
      }
      setError(data?.error || data?.details || 'Payment failed');
    },
  });

  // Payment mutation for public payments
  const payPublicMutation = useMutation({
    mutationFn: (data) => depositsAPI.payPublic(paymentToken, data),
    onSuccess: (response) => {
      setSuccess(true);
      setTimeout(() => {
        onSuccess?.(response.data);
      }, 1500);
    },
    onError: (err) => {
      setError(err.response?.data?.error || err.response?.data?.details || 'Payment failed');
    },
  });

  const isProcessing = payMutation.isPending || payPublicMutation.isPending;

  // Which card form to show. Public pages get the gateway from the deposit
  // response (no login); signed-in users ask the gateway endpoint.
  const { gateway: myGateway, publishableKey: myKey, stripeAccount: myAccount } = usePaymentGateway({ enabled: !isPublic && !gatewayProp });
  const gatewayInfo = gatewayProp || (isPublic ? { gateway: 'authorizenet' } : { gateway: myGateway, publishableKey: myKey, stripeAccount: myAccount });
  const hosted = isHostedGateway(gatewayInfo);
  // New details are being entered (anything but "pay with a saved method").
  const hostedNewDetails = hosted && !(!isPublic && paymentMethods.length > 0 && !useNewCard && !useNewBank);
  // Bank payments carry no card surcharge.
  const hostedAmount = useNewBank ? depositAmount : totalAmount;
  const beginHosted = () => {
    const body = { amount: hostedAmount, methodType: useNewBank ? 'ach' : 'card' };
    return isPublic ? depositsAPI.beginPayPublic(paymentToken, body) : depositsAPI.beginPay(opportunityId, body);
  };
  const payHosted = async ({ paymentIntentId }) => {
    setError('');
    const surcharge = useNewBank ? 0 : surchargeAmount;
    if (isPublic) {
      await payPublicMutation.mutateAsync(
        useNewBank
          ? { paymentMethod: 'ach', bankAccount: { paymentIntentId }, amount: hostedAmount, surchargeAmount: 0 }
          : { paymentMethod: 'card', card: { paymentIntentId }, amount: hostedAmount, surchargeAmount: surcharge }
      );
    } else {
      await payMutation.mutateAsync({ card: { paymentIntentId }, amount: hostedAmount, surchargeAmount: surcharge });
    }
  };

  // Don't render if not open - must be after all hooks
  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (isPublic) {
      if (useNewBank) {
        // Public ACH payment
        if (!bankData.routingNumber || !bankData.accountNumber || !bankData.nameOnAccount) {
          setError('Please enter routing number, account number, and name on account');
          return;
        }
        if (!billingAddress.firstName || !billingAddress.lastName || !billingAddress.address ||
            !billingAddress.city || !billingAddress.state || !billingAddress.zip) {
          setError('Please enter complete billing address');
          return;
        }
        payPublicMutation.mutate({
          paymentMethod: 'ach',
          bankAccount: {
            routingNumber: bankData.routingNumber,
            accountNumber: bankData.accountNumber,
            accountType: bankData.accountType,
            nameOnAccount: bankData.nameOnAccount,
            bankName: bankData.bankName,
          },
          billingAddress,
          amount: depositAmount, // no surcharge for ACH
          surchargeAmount: 0,
        });
      } else {
        // Public credit card payment
        if (!cardData.cardNumber || !cardData.expirationMonth || !cardData.expirationYear || !cardData.cvv) {
          setError('Please enter complete card information');
          return;
        }
        if (!billingAddress.firstName || !billingAddress.lastName || !billingAddress.address ||
            !billingAddress.city || !billingAddress.state || !billingAddress.zip) {
          setError('Please enter complete billing address');
          return;
        }
        payPublicMutation.mutate({
          paymentMethod: 'card',
          card: {
            cardNumber: cardData.cardNumber.replace(/\s/g, ''),
            expirationDate: `${cardData.expirationMonth}${cardData.expirationYear.slice(-2)}`,
            cvv: cardData.cvv,
          },
          billingAddress,
          amount: totalAmount,
          surchargeAmount: surchargeAmount,
        });
      }
    } else {
      // Authenticated payment
      if (useNewBank) {
        // Add ACH bank account, save it, then charge
        if (!bankData.routingNumber || !bankData.accountNumber || !bankData.nameOnAccount) {
          setError('Please enter routing number, account number, and name on account');
          return;
        }
        try {
          const addResult = await paymentsAPI.addBank({
            routingNumber: bankData.routingNumber,
            accountNumber: bankData.accountNumber,
            accountType: bankData.accountType,
            nameOnAccount: bankData.nameOnAccount,
            bankName: bankData.bankName || undefined,
            isDefault: false,
          }, { companyId });
          const newMethodId = addResult.data?.paymentMethod?.id || addResult.data?.id;
          if (!newMethodId) { setError('Failed to add bank account'); return; }
          payMutation.mutate({ paymentMethodId: newMethodId, amount: depositAmount, surchargeAmount: 0 });
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to add bank account');
        }
        return;
      }

      // Auth user entering a new credit card (no saved methods)
      if (!selectedMethodId) {
        if (!cardData.cardNumber || !cardData.expirationMonth || !cardData.expirationYear || !cardData.cvv) {
          setError('Please enter complete card information');
          return;
        }
        const cardPayload = {
          cardNumber: cardData.cardNumber.replace(/\s/g, ''),
          expirationDate: `${cardData.expirationMonth}${cardData.expirationYear.slice(-2)}`,
          cvv: cardData.cvv,
        };
        if (saveCard) {
          // Save card to profile first, then charge saved method
          try {
            const addResult = await paymentsAPI.addCard({
              ...cardPayload,
              expirationMonth: parseInt(cardData.expirationMonth),
              expirationYear: parseInt(cardData.expirationYear),
              billingAddress,
              isDefault: false,
            }, { companyId });
            const newMethodId = addResult.data?.paymentMethod?.id || addResult.data?.id;
            if (!newMethodId) { setError('Failed to save card'); return; }
            payMutation.mutate({ paymentMethodId: newMethodId, amount: totalAmount, surchargeAmount });
          } catch (err) {
            setError(err.response?.data?.error || 'Failed to save card');
          }
        } else {
          // One-time charge — not saved
          if (!billingAddress.firstName || !billingAddress.lastName || !billingAddress.address ||
              !billingAddress.city || !billingAddress.state || !billingAddress.zip) {
            setError('Please enter complete billing address');
            return;
          }
          payMutation.mutate({
            card: cardPayload,
            billingAddress,
            amount: totalAmount,
            surchargeAmount,
          });
        }
        return;
      }

      payMutation.mutate({
        paymentMethodId: selectedMethodId,
        amount: totalAmount,
        surchargeAmount: surchargeAmount,
      });
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 15 }, (_, i) => currentYear + i);

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-md w-full p-8 text-center max-h-[90vh] overflow-y-auto">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-xl font-semibold dark:text-white mb-2">Payment Successful!</h3>
          <p className="text-neutral-600 dark:text-neutral-400">
            Your payment of {formatCurrency(totalAmount)} has been processed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold dark:text-white flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary-500" />
            Pay Deposit
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Quote Info */}
        <div className="px-6 py-4 bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Quote</p>
          <p className="font-medium dark:text-white">{quoteName}</p>
          <div className="mt-3 space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-sm text-neutral-500 dark:text-neutral-400">Deposit Amount</span>
              <span className="font-medium dark:text-white">{formatCurrency(depositAmount)}</span>
            </div>
            {isCardPayment && surchargeAmount > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-500 dark:text-neutral-400">Card fee ({surchargeText})</span>
                <span className="text-neutral-600 dark:text-neutral-300">{formatCurrency(surchargeAmount)}</span>
              </div>
            )}
            <div className="flex justify-between items-center pt-2 border-t border-neutral-200 dark:border-neutral-700">
              <span className="font-medium dark:text-white">Total to Pay</span>
              <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                {formatCurrency(totalAmount)}
              </span>
            </div>
          </div>
        </div>

        {/* Payment Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {hostedNewDetails ? (
            /* Hosted gateway: the processor's secure form collects and confirms
               the card or bank account; only the confirmed payment id is posted. */
            <div className="space-y-4">
              {settings.allowACH && settings.allowCreditCard && (
                <div className="flex rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
                  <button type="button" onClick={() => setUseNewBank(false)}
                    className={`flex-1 py-2 text-sm font-medium ${!useNewBank ? 'bg-primary-600 text-white' : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300'}`}>
                    Credit Card
                  </button>
                  <button type="button" onClick={() => setUseNewBank(true)}
                    className={`flex-1 py-2 text-sm font-medium ${useNewBank ? 'bg-primary-600 text-white' : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300'}`}>
                    Bank Account (ACH)
                  </button>
                </div>
              )}
              {!isPublic && paymentMethods.length > 0 && (
                <button type="button" onClick={() => { setUseNewBank(false); setUseNewCard(false); }} className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
                  Use saved method
                </button>
              )}
              <HostedCardForm
                key={`${useNewBank ? 'ach' : 'card'}-${hostedAmount}`}
                publishableKey={gatewayInfo.publishableKey}
                stripeAccount={gatewayInfo.stripeAccount || null}
                mode="payment"
                begin={beginHosted}
                onConfirmed={payHosted}
                disabled={isProcessing}
                submitLabel={`Pay ${formatCurrency(hostedAmount)}`}
              />
            </div>
          ) : !isPublic && paymentMethods.length > 0 && !useNewCard && !useNewBank ? (
            /* Saved Payment Methods (Authenticated Users) */
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Payment Method
              </label>
              <div className="space-y-2">
                {paymentMethods.map((method) => {
                  const isCard = method.payment_type === 'card';
                  const brand = cardBrands[method.card_type] || cardBrands.unknown;
                  return (
                    <label
                      key={method.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedMethodId === method.id
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                          : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={method.id}
                        checked={selectedMethodId === method.id}
                        onChange={() => setSelectedMethodId(method.id)}
                        className="sr-only"
                      />
                      {isCard ? (
                        <div className={`w-10 h-6 rounded ${brand.color} flex items-center justify-center`}>
                          <CreditCard className="w-4 h-4 text-white" />
                        </div>
                      ) : (
                        <div className="w-10 h-6 rounded bg-green-600 flex items-center justify-center">
                          <Banknote className="w-4 h-4 text-white" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-neutral-900 dark:text-white">
                          {isCard
                            ? `${brand.label} ****${method.last_four}`
                            : `Bank ****${method.account_last_four}`
                          }
                        </p>
                        {isCard && method.exp_month && method.exp_year && (
                          <p className="text-sm text-neutral-500 dark:text-neutral-400">
                            Expires {method.exp_month}/{method.exp_year}
                          </p>
                        )}
                      </div>
                      {selectedMethodId === method.id && (
                        <CheckCircle className="w-5 h-5 text-primary-500" />
                      )}
                    </label>
                  );
                })}
              </div>
              {settings.allowACH && (
                <button
                  type="button"
                  onClick={() => { setUseNewBank(true); setSelectedMethodId(null); }}
                  className="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  + Add bank account (ACH)
                </button>
              )}
            </div>
          ) : !isPublic && useNewBank && paymentMethods.length > 0 ? (
            /* Auth user with saved methods explicitly adding a bank account */
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  New Bank Account (ACH)
                </label>
                <button type="button" onClick={() => setUseNewBank(false)} className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
                  Use saved method
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Routing Number *</label>
                  <input type="text" className="input" placeholder="021000021" maxLength={9}
                    value={bankData.routingNumber}
                    onChange={(e) => setBankData({ ...bankData, routingNumber: e.target.value.replace(/\D/g, '') })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Account Number *</label>
                  <input type="text" className="input" placeholder="Account number"
                    value={bankData.accountNumber}
                    onChange={(e) => setBankData({ ...bankData, accountNumber: e.target.value.replace(/\D/g, '') })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Account Type *</label>
                  <select className="input" value={bankData.accountType} onChange={(e) => setBankData({ ...bankData, accountType: e.target.value })}>
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Name on Account *</label>
                  <input type="text" className="input" placeholder="Full name"
                    value={bankData.nameOnAccount}
                    onChange={(e) => setBankData({ ...bankData, nameOnAccount: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Bank Name (optional)</label>
                <input type="text" className="input" placeholder="e.g. Chase, Wells Fargo"
                  value={bankData.bankName}
                  onChange={(e) => setBankData({ ...bankData, bankName: e.target.value })} />
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                This bank account will be saved to your account for future payments.
              </p>
            </div>
          ) : (
            /* Card/ACH tabs — used for: public users, and auth users with no saved methods */
            <>
              {/* Tab switcher when both methods available */}
              {settings.allowACH && settings.allowCreditCard && (
                <div className="flex rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden mb-1">
                  <button
                    type="button"
                    onClick={() => setUseNewBank(false)}
                    className={`flex-1 px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
                      !useNewBank ? 'bg-primary-600 text-white' : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700'
                    }`}
                  >
                    <CreditCard className="w-4 h-4" /> Credit Card
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseNewBank(true)}
                    className={`flex-1 px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
                      useNewBank ? 'bg-primary-600 text-white' : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700'
                    }`}
                  >
                    <Banknote className="w-4 h-4" /> ACH / Bank
                  </button>
                </div>
              )}

              {/* ACH form (public one-time or auth no-saved-methods) */}
              {useNewBank ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Routing Number *</label>
                      <input type="text" className="input" placeholder="021000021" maxLength={9}
                        value={bankData.routingNumber}
                        onChange={(e) => setBankData({ ...bankData, routingNumber: e.target.value.replace(/\D/g, '') })} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Account Number *</label>
                      <input type="text" className="input" placeholder="Account number"
                        value={bankData.accountNumber}
                        onChange={(e) => setBankData({ ...bankData, accountNumber: e.target.value.replace(/\D/g, '') })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Account Type *</label>
                      <select className="input" value={bankData.accountType} onChange={(e) => setBankData({ ...bankData, accountType: e.target.value })}>
                        <option value="checking">Checking</option>
                        <option value="savings">Savings</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Name on Account *</label>
                      <input type="text" className="input" placeholder="Full name"
                        value={bankData.nameOnAccount}
                        onChange={(e) => setBankData({ ...bankData, nameOnAccount: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Bank Name (optional)</label>
                    <input type="text" className="input" placeholder="e.g. Wells Fargo, Chase"
                      value={bankData.bankName}
                      onChange={(e) => setBankData({ ...bankData, bankName: e.target.value })} />
                  </div>
                </div>
              ) : (
              <>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Card Number
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder="1234 5678 9012 3456"
                  value={formatCardNumber(cardData.cardNumber)}
                  onChange={(e) => setCardData({ ...cardData, cardNumber: e.target.value })}
                  maxLength={19}
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Month
                  </label>
                  <select
                    className="input"
                    value={cardData.expirationMonth}
                    onChange={(e) => setCardData({ ...cardData, expirationMonth: e.target.value })}
                    required
                  >
                    <option value="">MM</option>
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                        {String(i + 1).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Year
                  </label>
                  <select
                    className="input"
                    value={cardData.expirationYear}
                    onChange={(e) => setCardData({ ...cardData, expirationYear: e.target.value })}
                    required
                  >
                    <option value="">YYYY</option>
                    {years.map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    CVV
                  </label>
                  <input
                    type="text"
                    className="input"
                    placeholder="123"
                    value={cardData.cvv}
                    onChange={(e) => setCardData({ ...cardData, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                    maxLength={4}
                    required
                  />
                </div>
              </div>

              {/* Save card checkbox — authenticated users only */}
              {!isPublic && !selectedMethodId && (
                <label className="flex items-center gap-3 cursor-pointer py-1">
                  <input
                    type="checkbox"
                    checked={saveCard}
                    onChange={(e) => setSaveCard(e.target.checked)}
                    className="w-4 h-4 rounded border-neutral-300 text-primary-500 focus:ring-primary-500"
                  />
                  <span className="text-sm text-neutral-700 dark:text-neutral-300">
                    Save this card for future payments
                  </span>
                </label>
              )}

              </>
              )}

              {/* Billing Address — shared for both ACH and credit card */}
              {(isPublic || (useNewCard) || (useNewBank && paymentMethods.length === 0)) && (
                <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4">
                  <h4 className="font-medium mb-3 dark:text-white">Billing Address</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">First Name *</label>
                      <input type="text" className="input" value={billingAddress.firstName}
                        onChange={(e) => setBillingAddress({ ...billingAddress, firstName: e.target.value })} required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Last Name *</label>
                      <input type="text" className="input" value={billingAddress.lastName}
                        onChange={(e) => setBillingAddress({ ...billingAddress, lastName: e.target.value })} required />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Company (optional)</label>
                    <input type="text" className="input" value={billingAddress.company}
                      onChange={(e) => setBillingAddress({ ...billingAddress, company: e.target.value })} />
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Address *</label>
                    <input type="text" className="input" value={billingAddress.address}
                      onChange={(e) => setBillingAddress({ ...billingAddress, address: e.target.value })} required />
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">City *</label>
                      <input type="text" className="input" value={billingAddress.city}
                        onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })} required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">State *</label>
                      <input type="text" className="input" placeholder="TX" maxLength={2}
                        value={billingAddress.state}
                        onChange={(e) => setBillingAddress({ ...billingAddress, state: e.target.value.toUpperCase() })} required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">ZIP *</label>
                      <input type="text" className="input" placeholder="12345" maxLength={10}
                        value={billingAddress.zip}
                        onChange={(e) => setBillingAddress({ ...billingAddress, zip: e.target.value })} required />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Phone (optional)</label>
                    <input type="tel" className="input" placeholder="(555) 123-4567" value={billingAddress.phone}
                      onChange={(e) => setBillingAddress({ ...billingAddress, phone: e.target.value })} />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Footer */}
          <div className="flex gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
              disabled={isProcessing}
            >
              Cancel
            </button>
            {!hostedNewDetails && <button
              type="submit"
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>Pay {formatCurrency(totalAmount)}</>
              )}
            </button>}
          </div>
        </form>
      </div>
    </div>
  );
}
